import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '@chicare/shared';
import { useMutation } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Logo } from '@/app/BuyerLayout';

/**
 * Resetting a console password.
 *
 * ── WHO THIS IS FOR, AND WHO IT IS NOT ──────────────────────────────────────
 *
 * Sellers, veterinary sellers, riders and ChiCare staff. They were handed a
 * password by a colleague at registration, and until this screen existed the
 * only way to change one was for another admin to do it from the console.
 *
 * That is fine on the morning somebody is hired. It is useless at 7am when a
 * pharmacist cannot get in to dispatch the day's orders and the person who
 * could reset it is asleep — a shop that cannot open, and orders that go
 * nowhere.
 *
 * Buyers never come here. Identity for a buyer is a phone number (§7.1) and
 * every sign-in is a fresh code, so there is no password to forget.
 *
 * ── WHY IT NEVER SAYS WHETHER THE ACCOUNT EXISTS ────────────────────────────
 *
 * The API answers identically for a known and an unknown address, and this
 * screen is built the same way on purpose. "No account with that email" would
 * hand anybody a way to ask which pharmacies and which veterinary clinics hold
 * ChiCare accounts — a commercial leak as much as a privacy one.
 *
 * So the copy after step one says what WOULD have happened, never what did.
 */
type Door = 'admin' | 'pharmacy' | 'rider';

const SIGN_IN: Record<Door, string> = {
  admin: '/admin/login',
  pharmacy: '/seller/login',
  rider: '/rider/login',
};

const MARK: Record<Door, string> = {
  admin: 'Admin',
  pharmacy: 'Seller',
  rider: 'Rider',
};

export function ForgotPasswordScreen({ door = 'pharmacy' }: { door?: Door }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  // Which half of the form is showing. Not derived from the request state:
  // asking for a second code must not throw away a code already typed.
  const [sent, setSent] = useState(false);

  const ask = useMutation({
    mutationFn: () => api.forgotPassword(email.trim()),
    onSuccess: () => setSent(true),
  });

  const reset = useMutation({
    mutationFn: () =>
      api.resetPassword({
        email: email.trim(),
        code: code.trim(),
        password,
        password_confirmation: confirmation,
      }),
    // Straight to the sign-in door. The reset deliberately returns no token,
    // so this is the first place the new password is actually proved to work.
    onSuccess: () => navigate(SIGN_IN[door], { replace: true, state: { passwordReset: true } }),
  });

  const active = sent ? reset : ask;
  const fieldErrors = active.error instanceof ApiError ? active.error.fieldErrors : {};

  const generalError =
    active.error instanceof ApiError && !active.error.isValidation ? active.error.message : null;

  return (
    <main className="grid min-h-dvh place-items-center bg-paper px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-[9px] bg-blue">
            <Logo className="h-5 w-5 fill-white" />
          </span>
          <span className="font-display text-[21px] font-extrabold tracking-[-0.02em] text-blue">
            ChiCare
          </span>
          <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            {MARK[door]}
          </span>
        </div>

        <div className="rounded-card border border-line bg-white p-5 sm:p-6">
          <h1 className="m-0 font-display text-[21px] font-bold tracking-[-0.02em]">
            {sent ? 'Set a new password' : 'Forgot your password'}
          </h1>

          <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">
            {sent
              ? 'Enter the code we sent, then choose a new password. The code expires in five minutes.'
              : 'Enter the email you sign in with. We will send a code to it, and to the phone number on the account.'}
          </p>

          {sent ? (
            <form
              className="mt-5 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                reset.mutate();
              }}
            >
              <Input
                label="The code"
                // Numeric keypad on a phone, and a six-digit code read off a
                // lock screen is worth the monospace.
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                placeholder="000000"
                className="font-mono tracking-[0.3em]"
                error={fieldErrors.code?.[0]}
                // Deliberately NOT maxLength={6}. The browser enforces that on
                // the raw string, BEFORE this strips the non-digits — so a code
                // pasted out of an SMS as "123 456" was truncated to "123 45"
                // and became 12345. Five digits, button stays disabled, and
                // nothing on screen says why. Trim after stripping instead.
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
              />

              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                hint="At least 10 characters."
                error={fieldErrors.password?.[0]}
                onChange={(event) => setPassword(event.target.value)}
              />

              <Input
                label="Type it again"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                error={fieldErrors.password_confirmation?.[0]}
                onChange={(event) => setConfirmation(event.target.value)}
              />

              {generalError ? <Problem>{generalError}</Problem> : null}

              {/* Said before they commit, not after. Somebody resetting from a
                  phone while signed in on the shop computer should know the
                  till is about to be signed out. */}
              <p className="m-0 flex items-start gap-1.5 text-[11px] leading-relaxed text-faint">
                <Icon name="alert" size={13} className="mt-px shrink-0 text-caution" />
                Setting a new password signs out every device your account is signed in on,
                including this one.
              </p>

              <Button
                full
                type="submit"
                loading={reset.isPending}
                disabled={code.length < 6 || !password || !confirmation}
              >
                Save the new password
              </Button>

              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setCode('');
                  reset.reset();
                }}
                className="min-h-9 w-full text-[12px] font-medium text-muted hover:text-blue"
              >
                Use a different email
              </button>
            </form>
          ) : (
            <form
              className="mt-5 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                ask.mutate();
              }}
            >
              <Input
                label="Email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                placeholder="you@yourpharmacy.ng"
                error={fieldErrors.email?.[0]}
                onChange={(event) => setEmail(event.target.value)}
              />

              {generalError ? <Problem>{generalError}</Problem> : null}

              <Button full type="submit" loading={ask.isPending} disabled={!email}>
                Send me a code
              </Button>
            </form>
          )}

          <p className="m-0 mt-4 text-[12px] leading-relaxed text-muted">
            <Link to={SIGN_IN[door]} className="font-semibold text-blue no-underline">
              Back to sign in
            </Link>
          </p>

          {/* Buyers land here by following a link meant for sellers, and a
              password screen with no password to reset is baffling. Say so. */}
          <p className="mt-3 border-t border-line pt-3 text-[11px] leading-relaxed text-faint">
            Buying on ChiCare rather than selling? You have no password — we send you a code each
            time.{' '}
            <Link to="/signin" className="font-semibold text-blue no-underline">
              Sign in with a code
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

function Problem({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-danger/25 bg-danger-tint px-3 py-2.5 text-[12px] text-danger"
    >
      <Icon name="alert" size={14} className="mt-px shrink-0" />
      <span>{children}</span>
    </div>
  );
}
